import {
  averageValues,
  extentFromValues,
} from "../core/math.js";

function parseCsvRecordEntries(text, { allowPartialFinalRecord = false, trimFields = false } = {}) {
  const input = String(text ?? "");
  const records = [];
  let values = [];
  let field = "";
  let rawRecord = "";
  let inQuotes = false;

  const cellValue = () => trimFields ? field.trim() : field;
  const pushField = () => {
    values.push(cellValue());
    field = "";
  };
  const pushRecord = () => {
    pushField();
    if (rawRecord.trim() || values.some((value) => String(value).trim() !== "")) {
      records.push({
        values,
        line: values.join(",").trim(),
      });
    }
    values = [];
    rawRecord = "";
  };

  for (let index = 0; index < input.length; index += 1) {
    const char = input[index];

    if (inQuotes) {
      rawRecord += char;
      if (char === "\"") {
        if (input[index + 1] === "\"") {
          field += "\"";
          rawRecord += input[index + 1];
          index += 1;
        } else {
          inQuotes = false;
        }
      } else {
        field += char;
      }
      continue;
    }

    if (char === "\"" && field.trim() === "") {
      rawRecord += char;
      inQuotes = true;
      continue;
    }

    if (char === ",") {
      rawRecord += char;
      pushField();
      continue;
    }

    if (char === "\r" || char === "\n") {
      if (char === "\r" && input[index + 1] === "\n") index += 1;
      pushRecord();
      continue;
    }

    rawRecord += char;
    field += char;
  }

  if (inQuotes) {
    if (allowPartialFinalRecord) return records;
    throw new Error("Unterminated quoted CSV field");
  }
  if (rawRecord.length || field.length || values.length) pushRecord();
  return records;
}

function splitUnquotedCsvLine(line) {
  const input = String(line ?? "");
  const values = [];
  let start = 0;
  for (let index = 0; index < input.length; index += 1) {
    if (input[index] !== ",") continue;
    values.push(input.slice(start, index).trim());
    start = index + 1;
  }
  values.push(input.slice(start).trim());
  return values;
}

export function parseCsvRecordRows(text, options: Record<string, any> = {}) {
  return parseCsvRecordEntries(text, options);
}

export function parseCsvRecords(text, options: Record<string, any> = {}) {
  return parseCsvRecordRows(text, options).map((record) => record.values);
}

export function parseCsv(text) {
  const [headers = [], ...lines] = parseCsvRecords(text);
  return lines.map((values) => {
    return Object.fromEntries(headers.map((header, index) => [header, values[index]]));
  });
}

export function splitCsvLine(line) {
  if (!String(line ?? "").includes("\"")) {
    return splitUnquotedCsvLine(line);
  }
  return parseCsvRecords(line, { trimFields: true })[0] ?? [];
}

export function normalizedHeaderToken(value) {
  return String(value ?? "")
    .replace(/^"|"$/g, "")
    .replace(/[^a-z0-9]/gi, "")
    .toLowerCase();
}

export function rowsFromHeader(text, headerMatcher, dataOffset = 1, preParsedRecords = null) {
  // preParsedRecords lets an ingest pass tokenize a multi-segment file once and
  // share the records across every segment instead of re-parsing the whole file.
  const records = preParsedRecords ?? parseCsvRecordRows(text, { trimFields: true });
  const headerIndex = records.findIndex((record) => headerMatcher(record.line, record.values, record));
  if (headerIndex === -1) throw new Error("Data header not found");
  const headers = records[headerIndex].values;
  const rows = records.slice(headerIndex + dataOffset).map(({ values }) => {
    return Object.fromEntries(headers.map((header, index) => [header, values[index] ?? ""]));
  });
  return { headers, rows, prefaceLines: records.slice(0, headerIndex).map((record) => record.line) };
}

export function rowsFromHeaderRange(text, headerMatcher, dataOffset = 1, startIndex = 0, endIndex = Infinity, preParsedRecords = null) {
  const records = preParsedRecords ?? parseCsvRecordRows(text, { trimFields: true });
  const headerIndex = records.findIndex((record) => headerMatcher(record.line, record.values, record));
  if (headerIndex === -1) throw new Error("Data header not found");
  const headers = records[headerIndex].values;
  const firstDataLine = headerIndex + dataOffset + Math.max(0, startIndex);
  const lastDataLine = Number.isFinite(endIndex)
    ? headerIndex + dataOffset + endIndex + 1
    : records.length;
  const rows = records.slice(firstDataLine, Math.min(records.length, lastDataLine)).map(({ values }) => {
    return Object.fromEntries(headers.map((header, index) => [header, values[index] ?? ""]));
  });
  return { headers, rows, prefaceLines: records.slice(0, headerIndex).map((record) => record.line) };
}

export function parseNumber(value) {
  const trimmed = String(value ?? "").trim();
  if (trimmed === "") return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
}

export function parseSignedNumber(value) {
  return parseNumber(String(value ?? "").replace(/^\+/, ""));
}

export function parseBoolean(value) {
  const normalized = String(value ?? "").trim().toLowerCase();
  if (!normalized) return null;
  if (["1", "true", "t", "yes", "y"].includes(normalized)) return true;
  if (["0", "false", "f", "no", "n"].includes(normalized)) return false;
  return null;
}

export function parseCoordinate(value) {
  const raw = String(value ?? "").trim();
  if (!raw || /^-+$/.test(raw) || /^n\/?a$/i.test(raw)) return null;

  const compact = raw.replace(/,/g, " ").replace(/\s+/g, " ").trim();
  // Plain decimal / scientific-notation values parse directly. Do this BEFORE
  // stripping hemisphere letters, so the 'E' in an exponent (e.g. "-1.10921E2" =
  // -110.921, from a spreadsheet round-trip) is not mistaken for an East marker.
  // DMS / hemisphere-lettered strings yield NaN here and fall through unchanged.
  const direct = Number(compact);
  if (Number.isFinite(direct) && Math.abs(direct) <= 180) return direct;
  // A coordinate string carries at most one letter, and it must be a
  // hemisphere. A CGR log with a flaky GPS feed writes tokens such as
  // "L 89 0.00" or "K 00 5.26"; reading those as unsigned degrees would
  // invent a fix. They are corrupt and parse as no coordinate.
  const letters = compact.match(/[A-Za-z]/g) ?? [];
  if (letters.length > 1 || (letters.length === 1 && !/[NSEW]/i.test(letters[0]))) return null;
  const hemisphere = compact.match(/[NSEW]/i)?.[0]?.toUpperCase() ?? "";
  const sign = hemisphere === "S" || hemisphere === "W" ? -1 : 1;
  const unsigned = compact.replace(/[NSEW]/gi, " ").replace(/\s+/g, " ").trim();
  const decimal = parseSignedNumber(unsigned);
  if (Number.isFinite(decimal) && Math.abs(decimal) <= 180) {
    return hemisphere ? sign * Math.abs(decimal) : decimal;
  }

  const parts = unsigned.match(/[+-]?\d+(?:\.\d+)?/g) ?? [];
  if (parts.length < 2) return null;

  const degrees = Number(parts[0]);
  let minutes = Number(parts[1]);
  const seconds = Number(parts[2] ?? 0);
  if (!Number.isFinite(degrees) || !Number.isFinite(minutes) || !Number.isFinite(seconds)) return null;

  if (!parts[1].includes(".") && parts[1].length > 2) {
    const minuteText = parts[1].padStart(4, "0");
    minutes = Number(`${minuteText.slice(0, -2)}.${minuteText.slice(-2)}`);
  }

  if (minutes >= 60 || seconds >= 60) return null;
  const coordinate = Math.abs(degrees) + minutes / 60 + seconds / 3600;
  const effectiveSign = degrees < 0 ? -1 : sign;
  return effectiveSign * coordinate;
}

// Axis-aware coordinate parsing. A latitude may only carry N or S and must
// fit 90 degrees; a longitude may only carry E or W and must fit 180. A
// hemisphere letter from the other axis (a longitude written "N 090 55.30")
// is a corrupt token, not a fix on the other side of the world.
function parseAxisCoordinate(value, hemispherePattern, limitDegrees) {
  const raw = String(value ?? "").trim();
  const compact = raw.replace(/,/g, " ").replace(/\s+/g, " ").trim();
  if (!Number.isFinite(Number(compact))) {
    const letters = compact.match(/[A-Za-z]/g) ?? [];
    if (letters.some((letter) => !hemispherePattern.test(letter))) return null;
  }
  const parsed = parseCoordinate(raw);
  return Number.isFinite(parsed) && Math.abs(parsed) <= limitDegrees ? parsed : null;
}

export function parseLatitude(value) {
  return parseAxisCoordinate(value, /[NS]/i, 90);
}

export function parseLongitude(value) {
  return parseAxisCoordinate(value, /[EW]/i, 180);
}

export function cToF(value) {
  return Number.isFinite(value) ? value * 9 / 5 + 32 : null;
}

export function valueFrom(row, names, parser = parseNumber) {
  for (const name of names) {
    if (Object.prototype.hasOwnProperty.call(row, name)) {
      const value = parser(row[name]);
      if (Number.isFinite(value)) return value;
    }
  }
  return null;
}

export function booleanFrom(row, names) {
  for (const name of names) {
    if (Object.prototype.hasOwnProperty.call(row, name)) {
      const value = parseBoolean(row[name]);
      if (typeof value === "boolean") return value;
    }
  }
  return null;
}

export function valueFromNormalized(row, names, parser = parseNumber) {
  const aliases = new Set(names.map(normalizedHeaderToken));
  for (const [name, rawValue] of Object.entries(row)) {
    if (!aliases.has(normalizedHeaderToken(name))) continue;
    const value = parser(rawValue);
    if (Number.isFinite(value)) return value;
  }
  return null;
}

export function normalizedBusVoltage(values) {
  const finite = values.filter(Number.isFinite);
  if (!finite.length) return null;
  if (finite.length === 1) return finite[0];

  const nonZero = finite.filter((value) => Math.abs(value) > 1);
  const candidates = nonZero.length ? nonZero : finite;
  const extent = extentFromValues(candidates);
  if (!extent) return null;

  const hasPlausibleBus = candidates.some((value) => value >= 8);
  if (hasPlausibleBus && extent.max >= extent.min * 1.7) return extent.max;
  return averageValues(candidates);
}

export function average(row, prefix, count) {
  let total = 0;
  let found = 0;
  for (let i = 1; i <= count; i += 1) {
    const value = parseNumber(row[`${prefix}${i}`]);
    if (value !== null) {
      total += value;
      found += 1;
    }
  }
  return found ? total / found : null;
}
